import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, Eye, EyeOff, CheckCircle, Circle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { checks: [], allPassed: false };
    const checks = [
      { label: 'At least 8 characters', passed: pwd.length >= 8 },
      { label: 'Uppercase letter', passed: /[A-Z]/.test(pwd) },
      { label: 'Lowercase letter', passed: /[a-z]/.test(pwd) },
      { label: 'Number', passed: /\d/.test(pwd) },
      { label: 'Special character', passed: /[!@#$%^&*()\-_+=[\]{}|;':",./<>?`~\\]/.test(pwd) },
    ];
    return { checks, allPassed: checks.every(c => c.passed) };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    const strength = getPasswordStrength(newPassword);
    if (!strength.allPassed) {
      toast({ title: 'Password does not meet requirements', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (error) {
      toast({
        title: 'Reset Failed',
        description: error.response?.data?.detail || 'Invalid or expired reset link.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Invalid Reset Link</h2>
          <p className="text-gray-400 mb-6">This password reset link is invalid or missing.</p>
          <Button onClick={() => navigate('/login')} className="bg-red-600 hover:bg-red-700">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden">
      <button
        onClick={() => navigate('/login')}
        className="absolute top-6 left-6 z-20 flex items-center space-x-2 text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm"
        data-testid="back-to-login-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Login</span>
      </button>

      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&q=80"
          alt="Books background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-900/30" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-black/80 backdrop-blur-lg p-8 rounded-lg shadow-2xl border border-white/10">
          <div className="flex items-center justify-center mb-8">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
              alt="Seven Chapter Books"
              className="h-28 sm:h-32 w-auto cursor-pointer"
              style={{ filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.85))', imageRendering: 'auto' }}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onClick={() => navigate('/')}
            />
          </div>

          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Password Reset!</h2>
              <p className="text-gray-400 mb-6">Your password has been updated successfully.</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white text-center mb-2">Reset Password</h2>
              <p className="text-gray-400 text-center text-sm mb-6">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-white">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                      required
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {newPassword && (
                    <div className="mt-2 space-y-1">
                      {getPasswordStrength(newPassword).checks.map((c, i) => (
                        <div key={i} className={`flex items-center gap-1.5 text-xs ${c.passed ? 'text-green-400' : 'text-zinc-500'}`}>
                          {c.passed ? <CheckCircle className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                          <span>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600"
                      required
                      data-testid="reset-confirm-password"
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 transition-all"
                  disabled={loading || !getPasswordStrength(newPassword).allPassed || newPassword !== confirmPassword}
                  data-testid="reset-password-submit"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
